import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Clock, User, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoPlayerPage = () => {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideo();
    fetchRelatedVideos();
  }, [videoId]);

  const fetchVideo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/${videoId}`);
      setVideo(response.data);
    } catch (error) {
      console.error('Failed to fetch video:', error);
      toast.error('Video not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos`);
      setRelatedVideos(response.data.filter(v => v.id !== videoId).slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch related videos:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse">
            <div className="aspect-video bg-sand rounded-xl mb-6" />
            <div className="h-8 bg-sand rounded w-1/2 mb-4" />
            <div className="h-4 bg-sand rounded w-full" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!video) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 text-center">
          <h1 className="font-heading text-2xl text-forest mb-4">Video Not Found</h1>
          <Link to="/videos">
            <Button className="btn-primary">Back to Video Library</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="video-player-page">
        <Link to="/videos" className="inline-flex items-center gap-2 text-forest hover:text-terracotta mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Video Library
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Video */}
          <div className="lg:col-span-2">
            {/* Video Player - handles Vimeo, YouTube, and uploaded videos */}
            <div className="mb-6">
              {/* Vimeo Embed */}
              {(video.video_type === 'vimeo' || (!video.video_type && video.vimeo_id)) && video.vimeo_id && (
                <div className="vimeo-container">
                  <iframe
                    src={`https://player.vimeo.com/video/${video.vimeo_id}?title=0&byline=0&portrait=0`}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={video.title}
                  />
                </div>
              )}

              {/* YouTube Embed */}
              {video.video_type === 'youtube' && video.youtube_id && (
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.youtube_id}?rel=0`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={video.title}
                    className="w-full h-full"
                  />
                </div>
              )}

              {/* Uploaded Video Player */}
              {video.video_type === 'upload' && video.video_file_url && (
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <video
                    controls
                    className="w-full h-full"
                    poster={video.thumbnail_url}
                  >
                    <source src={`${API_URL}${video.video_file_url}`} />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {/* Fallback for videos without proper type set */}
              {!video.vimeo_id && !video.youtube_id && !video.video_file_url && (
                <div className="aspect-video rounded-xl bg-sand flex items-center justify-center">
                  <p className="text-gray-500">Video not available</p>
                </div>
              )}
            </div>

            <Card className="card-base">
              <CardContent className="p-6">
                <span className="inline-block px-3 py-1 bg-sand text-forest text-sm rounded-full mb-3">
                  {video.category}
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-forest mb-4">
                  {video.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-gray-500 mb-6">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {video.instructor}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {video.duration}
                  </span>
                </div>
                
                <p className="text-gray-600 leading-relaxed">{video.description}</p>
              </CardContent>
            </Card>
          </div>

          {/* Related Videos */}
          <div>
            <h2 className="font-heading text-xl font-semibold text-forest mb-4">Related Videos</h2>
            <div className="space-y-4">
              {relatedVideos.map((relatedVideo) => (
                <Link to={`/videos/${relatedVideo.id}`} key={relatedVideo.id}>
                  <Card className="card-base overflow-hidden group" data-testid={`related-video-${relatedVideo.id}`}>
                    <div className="flex gap-4 p-3">
                      <div className="relative w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                        <img 
                          src={relatedVideo.thumbnail_url} 
                          alt={relatedVideo.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Play className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-forest line-clamp-2 group-hover:text-terracotta transition-colors">
                          {relatedVideo.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">{relatedVideo.instructor}</p>
                        <p className="text-xs text-gray-400 mt-1">{relatedVideo.duration}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VideoPlayerPage;
